import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/shared/PageHeader';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { ErrorAlert } from '../components/shared/ErrorAlert';
import { bazApi } from '../api/bazApi';
import { BAZ_DEFAULT_PAGE_SIZE, BAZ_SKILL_CATEGORIES, BAZ_SORT_OPTIONS } from '../config/baz';
export const BazSkillsPage = () => {
    const [skills, setSkills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [filters, setFilters] = useState({
        sortBy: 'popularity',
        sortOrder: 'desc',
    });
    const [pagination, setPagination] = useState({
        page: 1,
        perPage: BAZ_DEFAULT_PAGE_SIZE,
        total: 0,
    });
    const searchTimeoutRef = useRef(undefined);
    const loadSkills = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await bazApi.listSkills({
                page: pagination.page,
                perPage: pagination.perPage,
                filters,
            });
            setSkills(response.skills);
            setPagination((prev) => ({ ...prev, total: response.total }));
        }
        catch (err) {
            setError(err.response?.data?.message || 'Failed to load Baz skills');
        }
        finally {
            setLoading(false);
        }
    };
    const performSearch = async (query) => {
        if (!query.trim()) {
            loadSkills();
            return;
        }
        try {
            setIsSearching(true);
            setError(null);
            const response = await bazApi.searchSkills({
                query,
                filters,
                page: pagination.page,
                perPage: pagination.perPage,
            });
            setSkills(response.results);
            setPagination((prev) => ({ ...prev, total: response.total }));
        }
        catch (err) {
            setError(err.response?.data?.message || 'Failed to search skills');
        }
        finally {
            setIsSearching(false);
        }
    };
    useEffect(() => {
        if (searchQuery) {
            // Debounce search by 300ms
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            searchTimeoutRef.current = setTimeout(() => {
                performSearch(searchQuery);
            }, 300);
        }
        else {
            loadSkills();
        }
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery, filters, pagination.page]);
    const handleCategoryFilter = (category) => {
        setFilters((prev) => ({
            ...prev,
            category: prev.category === category ? undefined : category,
        }));
        setPagination((prev) => ({ ...prev, page: 1 }));
    };
    const handleSortChange = (sortBy) => {
        setFilters((prev) => ({ ...prev, sortBy }));
        setPagination((prev) => ({ ...prev, page: 1 }));
    };
    if (loading && skills.length === 0) {
        return (_jsx("div", { className: "flex items-center justify-center h-64", children: _jsx(LoadingSpinner, { size: "lg" }) }));
    }
    return (_jsxs("div", { children: [_jsx(PageHeader, { title: "Baz Skills Marketplace", subtitle: "Browse, search, and install Baz skills as agents in Cosmo" }), _jsx("div", { className: "card mb-6", children: _jsxs("div", { className: "flex items-center gap-4", children: [_jsx(Link, { to: "/baz/agents", className: "btn btn-secondary", children: "\uD83D\uDCE6 View Installed Agents" }), _jsx(Link, { to: "/baz/status", className: "btn btn-secondary", children: "\uD83D\uDCCA Integration Status" })] }) }), _jsx("div", { className: "card mb-6", children: _jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "flex items-center gap-4", children: [_jsxs("div", { className: "flex-1 relative", children: [_jsx("input", { type: "text", placeholder: "Search skills by name, description, or capabilities...", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), className: "input w-full" }), isSearching && (_jsx("div", { className: "absolute right-3 top-1/2 -translate-y-1/2", children: _jsx(LoadingSpinner, { size: "sm" }) }))] }), _jsx("select", { value: filters.sortBy, onChange: (e) => handleSortChange(e.target.value), className: "input w-48", children: BAZ_SORT_OPTIONS.map((option) => (_jsx("option", { value: option.value, children: option.label }, option.value))) })] }), _jsxs("div", { className: "flex flex-wrap gap-2", children: [_jsx("span", { className: "text-sm text-gray-600 font-medium mr-2", children: "Categories:" }), BAZ_SKILL_CATEGORIES.map((category) => (_jsx("button", { onClick: () => handleCategoryFilter(category), className: `px-3 py-1 rounded-full text-sm font-medium transition-colors ${filters.category === category
                                        ? 'bg-primary-600 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`, children: category }, category))), filters.category && (_jsx("button", { onClick: () => setFilters((prev) => ({ ...prev, category: undefined })), className: "px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200", children: "\u2715 Clear" }))] })] }) }), error && _jsx(ErrorAlert, { message: error, onRetry: loadSkills }), _jsx("div", { className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6", children: skills.map((skill) => (_jsxs(Link, { to: `/baz/skills/${skill.id}`, className: "card hover:shadow-lg transition-shadow cursor-pointer", children: [_jsxs("div", { className: "flex items-start justify-between mb-3", children: [_jsxs("div", { className: "flex-1", children: [_jsx("h3", { className: "text-lg font-semibold text-gray-900 mb-1", children: skill.name }), _jsx("span", { className: "inline-block px-2 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded", children: skill.category })] }), _jsxs("div", { className: "text-right", children: [_jsxs("div", { className: "flex items-center gap-1 text-yellow-500", children: [_jsx("span", { children: "\u2B50" }), _jsx("span", { className: "font-semibold", children: skill.rating.toFixed(1) })] }), _jsxs("div", { className: "text-xs text-gray-500 mt-1", children: [skill.installCount.toLocaleString(), " installs"] })] })] }), _jsx("p", { className: "text-sm text-gray-600 mb-4 line-clamp-3", children: skill.description }), skill.tags.length > 0 && (_jsxs("div", { className: "flex flex-wrap gap-1 mb-4", children: [skill.tags.slice(0, 3).map((tag) => (_jsxs("span", { className: "px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded", children: ["#", tag] }, tag))), skill.tags.length > 3 && (_jsxs("span", { className: "px-2 py-0.5 text-gray-500 text-xs", children: ["+", skill.tags.length - 3, " more"] }))] })), _jsxs("div", { className: "pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500", children: [_jsxs("span", { children: ["v", skill.version] }), _jsxs("span", { children: ["by ", skill.author] })] })] }, skill.id))) }), skills.length === 0 && !loading && (_jsx("div", { className: "text-center py-12", children: _jsx("p", { className: "text-gray-500", children: searchQuery
                        ? 'No skills found matching your search'
                        : 'No Baz skills available' }) })), pagination.total > pagination.perPage && (_jsxs("div", { className: "card mt-6 flex items-center justify-between", children: [_jsxs("div", { className: "text-sm text-gray-600", children: ["Showing ", (pagination.page - 1) * pagination.perPage + 1, " to", ' ', Math.min(pagination.page * pagination.perPage, pagination.total), " of", ' ', pagination.total, " skills"] }), _jsxs("div", { className: "flex gap-2", children: [_jsx("button", { onClick: () => setPagination((prev) => ({ ...prev, page: prev.page - 1 })), disabled: pagination.page === 1, className: "btn btn-secondary", children: "Previous" }), _jsx("button", { onClick: () => setPagination((prev) => ({ ...prev, page: prev.page + 1 })), disabled: pagination.page * pagination.perPage >= pagination.total, className: "btn btn-secondary", children: "Next" })] })] }))] }));
};
